// src/components/DevTools/DeviceSelector.tsx

import React from 'react'
import { Zap, Maximize2 } from 'lucide-react'
import { Device } from '../../types'
import { deviceCategories } from '../../data/devices'

interface DeviceSelectorProps {
  devices: Device[]
  selectedDevices: Device[]
  deviceRotations: Map<string, boolean>
  onToggleDevice: (device: Device) => void
  onLoadEssentials: () => void
  onFullscreen: (device: Device) => void
}

export function DeviceSelector({
  devices,
  selectedDevices,
  deviceRotations,
  onToggleDevice,
  onLoadEssentials,
  onFullscreen,
}: DeviceSelectorProps) {
  return (
    <div className="sidebar">
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={onLoadEssentials}
          className="preset-btn"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px',
          }}
          title="Load Essential Devices"
        >
          <Zap className="w-4 h-4" />
          Essentials
        </button>
      </div>

      {Object.entries(deviceCategories).map(([category, { title, icon }]) => {
        const categoryDevices = devices.filter(d => d.category === category)
        const selectedCount = selectedDevices.filter(d => d.category === category).length

        return (
          <div key={category} className="device-category">
            <div className="category-header">
              <div className="category-title">
                {icon}
                {title}
              </div>
              <div className="category-count">
                {selectedCount}/{categoryDevices.length}
              </div>
            </div>

            <div className="device-list">
              {categoryDevices.map(device => {
                const isSelected = selectedDevices.find(d => d.name === device.name)
                const isRotated = deviceRotations.get(device.name) || false
                const displayWidth = isRotated ? device.height : device.width
                const displayHeight = isRotated ? device.width : device.height

                return (
                  <div
                    key={device.name}
                    role="button"
                    tabIndex={0}
                    onClick={() => onToggleDevice(device)}
                    onKeyDown={(e) => e.key === 'Enter' && onToggleDevice(device)}
                    className={`device-item ${isSelected ? 'selected' : ''}`}
                  >
                    <div className="device-name">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {device.icon}
                        {device.name}
                      </span>
                      {isSelected && (
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            onFullscreen(device)
                          }}
                          className="action-btn"
                          title="Fullscreen"
                        >
                          <Maximize2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    <div className="device-specs">
                      <span>
                        {displayWidth} × {displayHeight}
                      </span>
                      <span>{device.os}</span>
                      {device.pixelRatio && <span>@{device.pixelRatio}x</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
